import type * as THREE from 'three';
import {
  UNIQUES,
  AFFINITY_TYPES,
  FORGE,
  GEAR_BASES,
  GEAR_BASE_IDS,
  GEAR_SETS,
  GEMS,
  GEM_ATTR,
  GEM_CAP_BP,
  GEM_CUTS,
  GEM_CUT_COST,
  GEM_CUT_KEYS,
  GEM_CUT_LEVEL,
  GEM_FUSE,
  GEAR_CAPS,
  POWER,
  SAME_RING_BP,
  SEAL_IDS,
  SLOT_LEVEL,
  TITAN_MARK_LEVEL,
  WEIGHT_SET_BP,
  attrValue,
  canWearBase,
  type GearAttr,
  type GearBaseId,
  type GearSlot,
  type GearTrait,
  type UniqueSlot,
  type WornSlot,
  MASON_HAMMER,
  ABSENT_CLOAK,
  RECIPE_BASE_IDS,
  RECIPE_SABER,
} from '@somnhold/content';
import {
  MAX_BAG,
  armoryShowcase,
  forgeCost,
  forgedOf,
  forgeLevel,
  twoHanded,
  gearMods,
  heroStats,
  itemAttrs,
  itemBase,
  itemNameKeys,
  wearBlock,
  weightSetOf,
  wornSlotFor,
  type CampaignCommand,
  type CampaignState,
  type ItemInstance,
  type PlayerState,
  heroName,
  calendarMarea,
  recipeDust,
} from '@somnhold/sim';
import { audio } from '../audio/sfx.ts';
import { getLang, reasonText, t } from '../i18n.ts';
import { h } from '../ui/dom.ts';
import { ICONS } from '../ui/icons.ts';
import { compileThumbs, thumbLater } from '../ui/thumbs.ts';
import { GEM_COLOR, effectBlock, forgeDrawer, giftNeed, settingBlock } from './armory.ts';
import { compareGear, type GearCompare } from './gear-compare.ts';
import { featBlock, featReady, featReadyText } from './feat-block.ts';
import { formSheet } from './form-cards.ts';
import { freshIds, markSeen, noteShown } from './fresh-gear.ts';
import { toastGlobal } from '../account.ts';
import { panoplyStrip, type PanoplyCtx } from './panoply-ui.ts';
import { offerPin } from './prep-pins.ts';

/*
 * The hero's gear (docs/EQUIPO.md §10): the miniature in the middle with its eight slots around it, each
 * piece drawn as its own little model; what is picked (a slot or a piece in the bag) opens in the
 * inspector beside it; the bag below as a grid. Pieces are drawn by the card renderer once their shaders
 * are compiled (never on the spot: the sheet used to freeze the bastion for up to a second).
 */

type Cmd = (c: CampaignCommand) => Promise<boolean>;

/** Rune colors by rarity, as the models paint them (no gold: grey, green, blue, violet, dragon red). */
export const RUNE: Record<string, string> = { common: '#c4c8d2', uncommon: '#47d16a', rare: '#3a8bff', veryRare: '#b05cff', unique: '#ff4f3a' };
const RARITY_ORDER: Record<string, number> = { unique: 0, veryRare: 1, rare: 2, uncommon: 3, common: 4 };
const ROMAN = ['', 'I', 'II', 'III', 'IV'];

/** The two columns around the miniature: what goes on the body on the left, in the hands and on the neck on the right. */
const LEFT: WornSlot[] = ['head', 'armor', 'boots', 'ring'];
const RIGHT: WornSlot[] = ['weapon', 'offhand', 'amulet', 'ring2'];
/** The sheet's miniature turned toward the viewer (the cards' usual turn shows the right side, the off hand behind). */
const FIGURE_YAW = 0.25;
const BAG_ORDER: GearSlot[] = ['weapon', 'offhand', 'head', 'armor', 'boots', 'amulet', 'ring'];

/** A slot's glyph: an empty slot's, and a piece's icon on its card. */
export const SLOT_GLYPH: Record<WornSlot, string> = {
  weapon: ICONS.sword,
  offhand: ICONS.shield,
  head: ICONS.helm,
  armor: ICONS.armor,
  boots: ICONS.boot,
  amulet: ICONS.amulet,
  ring: ICONS.dreamerRing,
  ring2: ICONS.dreamerRing,
};

/** A worn slot's name (the two rings numbered). */
export const wornLabel = (w: WornSlot): string => (w === 'ring' ? t('gear.ringN', { n: 1 }) : w === 'ring2' ? t('gear.ringN', { n: 2 }) : t(`slot.${w}`));

/** A piece's name: its base and suffix (docs/EQUIPO.md §9), or a unique's own. */
export const itemName = (item: ItemInstance, cls: PlayerState['hero']['heroClass']): string => {
  const k = itemNameKeys(item, cls);
  return k.suffix ? t('gear.name', { base: t(k.base), suffix: t(k.suffix) }) : t(k.base);
};

/** The model a piece's card draws. */
export const itemThumbKey = (item: ItemInstance, cls: PlayerState['hero']['heroClass']): string => (item.unique ? `unique:${item.unique}` : `gear:${itemBase(item, cls)}:${item.rarity}`);

/** A piece's picture: drawn in its turn, once its shaders are ready (the frame fades in; nothing stalls meanwhile). */
const pieceImg = (renderer: THREE.WebGLRenderer, key: string, size: number, cls = 'gp-img', yaw?: number): HTMLElement => {
  const img = h(`img.${cls}.loading`, { alt: '', draggable: 'false' }) as HTMLImageElement;
  thumbLater(
    renderer,
    key,
    size,
    (url) => {
      img.src = url;
      img.classList.remove('loading');
    },
    () => img.isConnected || !img.ownerDocument,
    yaw,
  );
  return img;
};

const pct = (bp: number): string => {
  const v = Math.round(bp / 10) / 10;
  return `${v > 0 ? '+' : v < 0 ? '−' : ''}${Math.abs(v).toLocaleString(getLang() === 'es' ? 'es-AR' : 'en-US', { maximumFractionDigits: 1 })}`;
};

/** A base's trait as lines of text, with its numbers from the content (at `f` bp of its strength). */
export const traitLines = (tr: GearTrait, res: ItemInstance['res'], f = 10000): string[] => {
  const v = (x: number): string => pct(Math.trunc((x * f) / 10000));
  const out: string[] = [];
  if (tr.melee) out.push(t('gear.trait.melee'));
  if (tr.attackBp) out.push(t('gear.trait.attack', { v: v(tr.attackBp) }));
  if (tr.attackSpeedBp) out.push(t('gear.trait.attackSpeed', { v: v(tr.attackSpeedBp) }));
  if (tr.rangeBp) out.push(t('gear.trait.range', { v: v(tr.rangeBp) }));
  if (tr.damageBp) out.push(t('gear.trait.damage', { v: v(tr.damageBp) }));
  if (tr.hpBp) out.push(t('gear.trait.hp', { v: v(tr.hpBp) }));
  if (tr.armorBp) out.push(t('gear.trait.armor', { v: v(tr.armorBp) }));
  if (tr.speedBp) out.push(t('gear.trait.speed', { v: v(tr.speedBp) }));
  if (tr.cooldownBp) out.push(t('gear.trait.cooldown', { v: pct(-Math.trunc((tr.cooldownBp * f) / 10000)) }));
  if (tr.critBp) out.push(t('gear.trait.crit', { v: v(tr.critBp) }));
  if (tr.bigFoeBp) out.push(t('gear.trait.bigFoe', { v: v(tr.bigFoeBp) }));
  if (tr.cleaveBp) out.push(t('gear.trait.cleave', { v: Math.round(tr.cleaveBp / 100), fx: t('effect.cleave') }));
  if (tr.stunEvery) out.push(t('gear.trait.stun', { n: tr.stunEvery, s: ((tr.stunTicks ?? 0) / 12).toLocaleString(getLang() === 'es' ? 'es-AR' : 'en-US', { maximumFractionDigits: 1 }) }));
  if (tr.pierce) out.push(t('gear.trait.pierce'));
  if (tr.blockBp) out.push(t('gear.trait.block', { v: Math.round(tr.blockBp / 100) }));
  if (tr.skillBp) out.push(t('gear.trait.skill', { v: v(tr.skillBp) }));
  if (tr.healBp) out.push(t('gear.trait.heal', { v: v(tr.healBp) }));
  if (tr.summonBp) out.push(t('gear.trait.summon', { v: v(tr.summonBp) }));
  if (tr.resBp) out.push(t('gear.trait.res', { v: v(tr.resBp), type: t(`dtype.${res ?? AFFINITY_TYPES[0]}`) }));
  if (tr.mendBp) out.push(t('gear.trait.mend', { v: Math.round(tr.mendBp / 100), c: Math.round(MASON_HAMMER.capBp / 100) }));
  if (tr.shun) out.push(t('gear.trait.shun'));
  if (tr.absent) out.push(t('gear.trait.absent', { v: Math.round(ABSENT_CLOAK.ghostXpBp / 100) - 100 }));
  return out;
};

/** The base's kind as a short tag: one or two hands for weapons, the weight for what is worn on the body. */
const baseTag = (id: GearBaseId): string => {
  const b = GEAR_BASES[id];
  if (b.hands) return t(`gear.hands.${b.hands}`);
  if (b.weight) return t(`weight.${b.weight}`);
  return '';
};

/** What the sheet keeps across rerenders: what is picked (a worn slot or a bag piece) and the bag's filter. */
const view: { at: WornSlot | null; bag: number; filter: 'all' | GearSlot; armory: boolean } = { at: 'weapon', bag: 0, filter: 'all', armory: false };
/** The piece just forged plays its flash once. */
let flashed = 0;
export const flashGear = (id: number): void => {
  flashed = id;
};
/** Picks a bag piece in the sheet (a gift's notice opens the sheet on it), with the whole bag in view. */
export const pickBag = (id: number): void => {
  view.bag = id;
  view.at = null;
  view.filter = 'all';
};
/** Bag pieces not looked at yet, as the sheet last worked them out. */
let fresh = new Set<number>();
/** Picks a worn slot in the sheet (the sheet opens on it next time). */
export const pickSlot = (w: WornSlot): void => {
  view.at = w;
  view.bag = 0;
};

const attrText = (k: GearAttr, v: number): string => t(`attr.${k}`, { v: Math.round(attrValue(k, v) / 10) / 10 });

/**
 * What a piece's «+% damage» puts on the hero's Attack. Every damage bonus adds up and lands once on the base
 * attack, so a +18 % is worth less than 18 % of the Attack shown: the sheet says it in points.
 */
const attackFrom = (hero: PlayerState['hero'], item: ItemInstance, dmg: number): number => {
  const g = gearMods(hero);
  const worn = Object.values(hero.gear).some((x) => x?.id === item.id);
  const atk = (damageBp: number): number => heroStats({ pid: '', name: '', cls: hero.heroClass, level: hero.level, control: 'live', powerBp: 10000, post: 0, gear: { ...g, damageBp } }).attack;
  const withIt = g.damageBp + (worn ? 0 : dmg);
  return atk(withIt) - atk(withIt - dmg);
};

/** The piece's attributes as rows: each one's value, what it adds to the Attack, and what the forge put in it. */
const attrRows = (item: ItemInstance, hero: PlayerState['hero']): HTMLElement => {
  const forged = forgedOf(item);
  const a = itemAttrs(item);
  return h(
    'ul.gi-attrs',
    {},
    ...(Object.keys(a).sort() as GearAttr[]).map((k) => {
      const n = forged[k] ?? 0;
      const pts = k === 'damage' ? attackFrom(hero, item, a[k] ?? 0) : 0;
      return h(
        'li',
        {},
        h('span', {}, attrText(k, a[k] ?? 0), pts > 0 ? h('span.gi-atk', { 'data-test': 'attack-from', title: t('attr.damageAttackHint') }, ` · ${t('attr.damageAttack', { n: pts })}`) : ''),
        n > 0 ? h('span.gi-forged', { title: t('gear.forgedHint') }, `+${n}`) : '',
      );
    }),
  );
};

/** A bag piece next to the one worn where it would go: one chip per change, then the powers won or lost. */
const compareBlock = (c: GearCompare, worn: ItemInstance | undefined, cls: PlayerState['hero']['heroClass']): HTMLElement => {
  const shown = (bp: number): number => Math.round(bp / 10) / 10;
  const val = (k: string, bp: number): string => `${k === 'cooldown' && bp ? '−' : ''}${shown(bp)}`;
  const chips = c.attrs.map((d) => {
    const diff = shown(d.mine) - shown(d.worn);
    const dir = diff > 0 ? 'up' : diff < 0 ? 'down' : 'same';
    return h(`span.cmp-chip.${dir}`, {}, dir !== 'same' ? h('i', { 'aria-hidden': 'true' }, dir === 'up' ? '▲' : '▼') : '', h('span.cmp-name', {}, t(`attrName.${d.attr}`)), h('b', {}, worn && dir !== 'same' ? `${val(d.attr, d.worn)} → ${val(d.attr, d.mine)} %` : `${val(d.attr, d.mine)} %`));
  });
  const power = (kind: string, key: string): string => (kind === 'unique' ? t(`unique.${key}`) : kind === 'trait' ? t(`gear.base.${key}`) : t(`effect.${key}`));
  return h(
    'div.cmp',
    { 'data-test': 'gear-compare' },
    h('p.small.cmp-vs', {}, worn ? t('compare.vs', { what: itemName(worn, cls) }) : t('compare.emptyHint')),
    h(
      'div.cmp-chips',
      {},
      ...chips,
      ...c.powers.map((p) => h(`span.cmp-chip.cmp-power.${p.gain ? 'up' : 'down'}`, { title: t(p.gain ? 'compare.gain' : 'compare.lose', { what: power(p.kind, p.key) }) }, h('i', { 'aria-hidden': 'true' }, p.gain ? '+' : '−'), h('b', {}, power(p.kind, p.key)))),
    ),
  );
};

export interface GearCtx {
  s: CampaignState;
  p: PlayerState;
  renderer: THREE.WebGLRenderer;
  cmd: Cmd;
  rerender: () => void;
  /** The forge's cap on pluses (0: no forge standing). */
  cap: number;
  /** A forge is built but lies in ruins: what it does waits for its repair. */
  ruined: boolean;
}

/** Whether the bastion has a forge but none of them stands. */
export const forgeRuined = (s: CampaignState): boolean => Object.values(s.cells).some((cell) => cell.built?.structure === 'forge') && !forgeLevel(s);

/** The line that says the forge is down, when it is. */
const ruinedNote = (c: GearCtx): HTMLElement | string => (c.ruined ? h('p.small.hint.forge-ruined', { 'data-test': 'forge-ruined' }, t('forge.ruined')) : '');

/** The top of the inspector: the piece large, its name in its rarity's color, and what kind of piece it is. */
const inspectHead = (c: GearCtx, item: ItemInstance, at: string): HTMLElement => {
  const cls = c.p.hero.heroClass;
  const base = itemBase(item, cls);
  const tag = baseTag(base);
  const bits = [item.awaken ? t('uawaken.stage', { n: ROMAN[item.awaken] ?? '' }) : t(`rarity.${item.rarity}`), item.masterwork ? t('masterwork.badge') : '', at, tag].filter(Boolean);
  return h(
    'div.gi-head',
    {},
    h('div.gi-pic', {}, pieceImg(c.renderer, itemThumbKey(item, cls), 192, 'gi-img')),
    h(
      'div.gi-title',
      {},
      h('h4.gi-name', { 'data-test': 'item-name' }, itemName(item, cls), item.plus ? h('span.gi-plus', {}, ` +${item.plus}`) : ''),
      h('div.gi-sub', {}, bits.join(' · ')),
      item.power !== undefined ? h('div.gi-power', { title: t('gear.powerHint') }, h('span', { html: ICONS.sparkle }), t('gear.power', { n: item.power })) : '',
    ),
  );
};

/** A set piece's block: the set, how many of it are worn, and what two and all three give (ticked when worn). */
const setBox = (c: GearCtx, item: ItemInstance): HTMLElement | '' => {
  if (!item.set) return '';
  const def = GEAR_SETS[item.set];
  const worn = Object.values(c.p.hero.gear).filter((x) => x?.set === item.set).length;
  const line = (n: number, txt: string): HTMLElement => h(`li${worn >= n ? '.on' : ''}`, {}, h('span.mark', { 'aria-hidden': 'true' }, worn >= n ? '✓' : '·'), txt);
  return h(
    'div.gi-set',
    { 'data-test': 'set' },
    h('span.gi-label', {}, t('gear.setName', { set: t(`set.${item.set}`) }), h('span.gi-boost', {}, ` ${worn}/${def.slots.length}`)),
    h('ul', {}, line(2, t('gear.setTwo', { what: traitLines(def.two, undefined).join(' · ') })), line(def.slots.length, t('gear.setThree', { what: [...traitLines(def.three, undefined), t(`effect.${def.effect}`)].join(' · ') }))),
    h('p.small', {}, t('gear.setWhere', { place: t(`set.${item.set}.place`), n: def.level })),
  );
};

/** The trait box: what this base does, whatever its rolls. */
const traitBox = (c: GearCtx, item: ItemInstance, at: WornSlot | null): HTMLElement => {
  const h0 = c.p.hero;
  if (item.unique) return h('p.small.gi-trait-none', {}, t('gear.trait.uniqueNone'));
  if (!item.base) return h('p.small.gi-trait-none', {}, t('gear.trait.legacy'));
  // Worn, a weight set or a second ring of one form changes how much it counts.
  let f = 10000;
  if (at && (at === 'head' || at === 'armor' || at === 'boots') && weightSetOf(h0)) f += WEIGHT_SET_BP;
  if (at === 'ring2' && h0.gear.ring?.base === item.base) f = SAME_RING_BP;
  const lines = traitLines(GEAR_BASES[item.base].trait, item.res, f);
  if (!lines.length) return h('p.small.gi-trait-none', {}, t('gear.trait.none'));
  return h('div.gi-trait', {}, h('span.gi-label', {}, t('gear.trait.title'), f > 10000 ? h('span.gi-boost', {}, ` ${t('gear.trait.boosted', { n: Math.round((f - 10000) / 100) })}`) : ''), h('ul', {}, ...lines.map((l) => h('li', {}, l))));
};

/** The forge's plus, one button per attribute (the player picks where it goes). */
const forgePick = (c: GearCtx, item: ItemInstance): HTMLElement | '' => {
  if (!c.cap || item.plus >= FORGE.maxPlus) return '';
  if (item.plus >= c.cap) return h('p.small.hint', {}, t('hero.forgeCap', { n: item.plus + 1, l: FORGE.maxPlusByLevel.findIndex((m) => m > item.plus) + 1 }));
  return h(
    'div.forge-pick',
    {},
    h('span.small', {}, t('hero.forgeNext', { n: item.plus + 1 }), ' ', h('span.cost-chip', {}, h('span', { html: ICONS.dust }), String(forgeCost(item)))),
    ...(Object.keys(item.attrs).sort() as GearAttr[]).map((k) =>
      h(
        'button.btn.small.stone',
        {
          'data-test': 'forge',
          onclick: async () => {
            if (await c.cmd({ t: 'forge', item: item.id, attr: k })) {
              flashed = item.id;
              audio.play('build');
              c.rerender();
            }
          },
        },
        t(`attr.${k}`, { v: attrValue(k, FORGE.perPlusBp) / 100 }),
      ),
    ),
  );
};

/** Melting a piece: dust back to the common storage. */
const salvageDust = (item: ItemInstance, dust: Record<string, number>, plusBp: number): number => {
  let plus = 0;
  for (let k = 0; k < item.plus; k++) plus += FORGE.dustPerLevel * (k + 1);
  return (dust[item.rarity] ?? 0) + Math.trunc((plus * plusBp) / 10000);
};

/** Where a unique piece's glass or bone came from (the armours from the dragon, the amulets from the Drowned Dragon). */
const uniqueLore = (item: ItemInstance): HTMLElement | '' => {
  if (!item.unique) return '';
  const key = `unique.${item.unique}.lore`;
  const txt = t(key);
  return txt && txt !== key ? h('p.small.unique-lore', {}, txt) : '';
};

/** A slot unique pieces go in (docs/PANOPLIA.md §1). */
const uniqueSlot = (at: WornSlot): at is UniqueSlot => at === 'weapon' || at === 'armor' || at === 'amulet';

/** What the panoply's screens need of the sheet. */
const pctx = (c: GearCtx): PanoplyCtx => ({ s: c.s, p: c.p, renderer: c.renderer, cmd: c.cmd, rerender: c.rerender, forge: !!c.cap });

/** The inspector for a worn piece: its card, the forge and the deeper work. */
const inspectWorn = (c: GearCtx, at: WornSlot, item: ItemInstance): HTMLElement =>
  h(
    `section.gi${flashed === item.id ? '.forged-now' : ''}`,
    { 'data-test': 'inspector', 'data-slot': at, style: `--rune:${RUNE[item.rarity]}` },
    inspectHead(c, item, wornLabel(at)),
    // «Tus únicas», right under the slot's header (docs/PANOPLIA.md §9.1).
    uniqueSlot(at) ? panoplyStrip(pctx(c), at) : '',
    traitBox(c, item, at),
    setBox(c, item),
    attrRows(item, c.p.hero),
    effectBlock(c.p, item),
    settingBlock(item),
    item.unique ? h('p.small.effect.unique-fx', {}, t(`unique.${item.unique}.desc`)) : '',
    uniqueLore(item),
    item.unique ? featBlock(c.s, c.p.hero, item) : '',
    item.bound ? h('p.small.bound', { 'data-test': 'bound' }, t('gear.bound')) : '',
    item.unique && UNIQUES[item.unique].cls === c.p.hero.heroClass && (at === 'weapon' || at === 'armor' || at === 'amulet') ? formSheet(c.p.hero, at, item.unique, item.awaken ?? 0) : '',
    item.unique ? h('p.small.bound', {}, t('unique.bound'), ' ', t('unique.follows')) : '',
    forgePick(c, item),
    c.cap ? forgeDrawer(c.s, c.p, item, c.cmd, c.rerender) : ruinedNote(c),
  );

/** The inspector for a bag piece: its card next to what it would replace, and what can be done with it. */
const inspectBag = (c: GearCtx, item: ItemInstance, salvage: { dust: Record<string, number>; plusBp: number }): HTMLElement => {
  const hero = c.p.hero;
  const cls = hero.heroClass;
  const at = wornSlotFor(hero, item);
  const worn = hero.gear[at];
  const block = wearBlock(hero, item, at);
  const friends = c.s.players.filter((x) => x.id !== c.p.id && !x.left);
  const needs = new Map(friends.map((f) => [f.id, giftNeed(f, item)]));
  const order = friends.slice().sort((x, y) => Number(!needs.get(x.id)) - Number(!needs.get(y.id)) || x.hero.level - y.hero.level);
  const equip = async (ring?: 0 | 1): Promise<void> => {
    if (await c.cmd({ t: 'equip', item: item.id, ...(ring !== undefined ? { ring } : {}) })) {
      audio.play('build', 0.4);
      offerPin(hero, ring === 1 ? 'ring2' : at, item, c.cmd, c.rerender);
      view.at = ring === 1 ? 'ring2' : at;
      view.bag = 0;
      c.rerender();
    }
  };
  // Two rings open: say which one it goes to.
  const twoRings = item.slot === 'ring' && hero.level >= SLOT_LEVEL.ring2;
  const equipBtns = block
    ? [
        h('p.small.gi-block', { 'data-test': 'wear-block' }, reasonText(block) ?? t('reason.generic'), block === 'tooPowerful' && item.power !== undefined ? ` ${t('gear.reachAt', { n: item.power - POWER.slack })}` : ''),
        // Not for this class: who in the group it suits (docs/EQUIPO.md §6).
        block === 'notYourClass' && item.base
          ? h('p.small.gi-suits', { 'data-test': 'suits' }, t('gear.suits', { who: friends.filter((f) => canWearBase(f.hero.heroClass, item.base as GearBaseId)).map((f) => `${heroName(f)} (${t(`class.${f.hero.heroClass}`).toLowerCase()})`).join(', ') || t('gear.suitsNobody') }))
          : '',
      ]
    : twoRings
      ? [h('button.btn.small', { 'data-test': 'equip', onclick: () => void equip(0) }, t('gear.equipRing', { n: 1 })), h('button.btn.small', { 'data-test': 'equip2', onclick: () => void equip(1) }, t('gear.equipRing', { n: 2 }))]
      : [h('button.btn.small', { 'data-test': 'equip', onclick: () => void equip() }, t('hero.equip'))];
  return h(
    `section.gi${flashed === item.id ? '.forged-now' : ''}`,
    { 'data-test': 'inspector', 'data-bag': String(item.id), style: `--rune:${RUNE[item.rarity]}` },
    inspectHead(c, item, t('gear.inBag')),
    giftFrom(c, item),
    traitBox(c, item, null),
    setBox(c, item),
    attrRows(item, c.p.hero),
    effectBlock(c.p, item),
    settingBlock(item),
    item.unique ? h('p.small.effect.unique-fx', {}, t(`unique.${item.unique}.desc`)) : '',
    uniqueLore(item),
    item.unique ? featBlock(c.s, hero, item) : '',
    item.bound ? h('p.small.bound', { 'data-test': 'bound' }, t('gear.bound')) : '',
    block ? '' : compareBlock(compareGear(item, worn), worn, cls),
    h(
      'div.gi-actions',
      {},
      ...equipBtns,
      c.cap && !item.unique
        ? h('button.btn.small.ghost', { 'data-test': 'salvage', title: t('hero.salvageHint'), onclick: async () => { if (await c.cmd({ t: 'salvage', item: item.id })) { view.bag = 0; c.rerender(); } } }, t('hero.salvage'), ' ', h('span.cost-chip', {}, h('span', { html: ICONS.dust }), `+${salvageDust(item, salvage.dust, salvage.plusBp)}`))
        : c.ruined && !item.unique
          ? h('button.btn.small.ghost', { 'data-test': 'salvage', disabled: true, title: t('forge.ruined') }, t('hero.salvage'), ' ', h('span.cost-chip', {}, h('span', { html: ICONS.dust }), `+${salvageDust(item, salvage.dust, salvage.plusBp)}`))
          : '',
    ),
    // A spare piece can take its pluses before it is worn (the command finds it in the bag too).
    forgePick(c, item),
    c.cap ? forgeDrawer(c.s, c.p, item, c.cmd, c.rerender) : ruinedNote(c),
    friends.length && !item.unique && !item.bound
      ? h(
          'div.bag-gift',
          {},
          h('span.small', {}, t('hero.giftTo')),
          ...order.map((f) =>
            h(
              `button.btn.small.ghost.gift-to${needs.get(f.id) ? '.needs' : ''}`,
              {
                'data-test': 'gift',
                onclick: async () => {
                  if (!(await c.cmd({ t: 'gift', item: item.id, to: f.id }))) return;
                  // Said where it went (the friend gets a notice of their own).
                  toastGlobal(t('gift.sent', { what: itemName(item, cls), who: heroName(f) }));
                  audio.play('click', 0.5);
                  view.bag = 0;
                  c.rerender();
                },
              },
              heroName(f),
              h('span.small', {}, ` · ${t(`class.${f.hero.heroClass}`)}`),
              needs.get(f.id) ? h('span.gift-need', {}, needs.get(f.id) as string) : '',
            ),
          ),
        )
      : '',
  );
};

/** Who sent a gifted piece (a friend who left the campaign still counts). */
const giftFrom = (c: GearCtx, item: ItemInstance): HTMLElement | string => {
  const who = item.from ? c.s.players.find((x) => x.id === item.from) : undefined;
  return who ? h('p.small.gi-from', { 'data-test': 'gift-from' }, h('span', { html: ICONS.chest }), t('gift.from', { who: heroName(who) })) : '';
};

/** The inspector for a slot with nothing in it: what goes there, what in the bag fits, or when it opens. */
const inspectEmpty = (c: GearCtx, at: WornSlot): HTMLElement => {
  const hero = c.p.hero;
  const locked = hero.level < SLOT_LEVEL[at];
  const held = at === 'offhand' && twoHanded(hero);
  const fits = hero.bag.filter((x) => wornSlotFor(hero, x, at === 'ring2' ? 1 : at === 'ring' ? 0 : undefined) === at && !wearBlock(hero, x, at));
  return h(
    `section.gi.gi-empty${locked ? '.locked' : ''}`,
    { 'data-test': 'inspector', 'data-slot': at },
    h('div.gi-head', {}, h('div.gi-pic.empty', { html: locked ? ICONS.lock : SLOT_GLYPH[at] }), h('div.gi-title', {}, h('h4.gi-name', {}, wornLabel(at)), h('div.gi-sub', {}, locked ? t('gear.lockedAt', { n: SLOT_LEVEL[at] }) : held ? t('gear.heldByTwo') : t('gear.emptySlot')))),
    h('p.small', {}, t(`gear.what.${at === 'ring2' ? 'ring' : at}`)),
    !locked && uniqueSlot(at) ? panoplyStrip(pctx(c), at) : '',
    !locked && fits.length
      ? h(
          'div.gi-fits',
          {},
          h('span.small', {}, t('gear.fits')),
          h('div.gs-bag', {}, ...fits.map((x) => bagTile(c, x))),
        )
      : '',
    locked ? '' : h('p.small.hint', {}, t('hero.gearFrom')),
  );
};

/** A piece in a grid: its picture in its rarity's frame, its plus, and whether it beats what is worn. */
const bagTile = (c: GearCtx, item: ItemInstance): HTMLElement => {
  const hero = c.p.hero;
  const at = wornSlotFor(hero, item);
  const can = !wearBlock(hero, item, at);
  const v = can ? compareGear(item, hero.gear[at]).verdict : null;
  const on = view.bag === item.id;
  const isNew = fresh.has(item.id) && !on;
  // A new piece glows until looked at: hovering it is enough with a mouse (no redraw: the glow just goes).
  const seen = (el: HTMLElement): void => {
    if (!fresh.delete(item.id)) return;
    el.classList.remove('fresh');
    markSeen(c.s, c.p, [item.id]);
  };
  return h(
    `button.gtile${on ? '.sel' : ''}${can ? '' : '.cant'}${isNew ? '.fresh' : ''}`,
    {
      style: `--rune:${RUNE[item.rarity]}`,
      title: isNew ? `${itemName(item, hero.heroClass)} · ${t('bag.fresh')}` : itemName(item, hero.heroClass),
      'data-test': 'bag-row',
      'data-rarity': item.rarity,
      ...(v ? { 'data-verdict': v } : {}),
      ...(isNew ? { 'data-fresh': '1' } : {}),
      'aria-pressed': String(on),
      // A short hover, not one passing over on the way elsewhere.
      ...(isNew ? { onpointerenter: ((ev: PointerEvent) => { const el = ev.target as HTMLElement; if (ev.pointerType === 'mouse') window.setTimeout(() => el.matches(':hover') && seen(el), 350); }) as EventListener } : {}),
      onclick: () => {
        if (fresh.delete(item.id)) markSeen(c.s, c.p, [item.id]);
        view.bag = item.id;
        view.at = null;
        c.rerender();
        // On a phone the inspector sits above: bring it into view.
        requestAnimationFrame(() => document.querySelector('.gs-inspect')?.scrollIntoView({ block: 'start', behavior: 'smooth' }));
      },
    },
    pieceImg(c.renderer, itemThumbKey(item, hero.heroClass), 128),
    item.plus ? h('span.gt-plus', {}, `+${item.plus}`) : '',
    v === 'better' || v === 'empty' ? h('span.gt-up', { title: t('compare.betterHint') }, '▲') : '',
    can ? '' : h('span.gt-lock', { html: ICONS.lock }),
    isNew ? h('span.gt-new', { 'aria-hidden': 'true' }, item.from ? h('span', { html: ICONS.chest }) : '', t('bag.fresh')) : '',
  );
};

/** One slot around the miniature. */
const slotTile = (c: GearCtx, at: WornSlot): HTMLElement => {
  const hero = c.p.hero;
  const item = hero.gear[at];
  const locked = hero.level < SLOT_LEVEL[at];
  const held = at === 'offhand' && !item && twoHanded(hero);
  const on = view.at === at;
  return h(
    `button.gslot${on ? '.sel' : ''}${item ? '.full' : ''}${locked ? '.locked' : ''}${held ? '.held' : ''}${item?.awaken ? '.awake' : ''}${featReady(hero, item) ? '.feat-ready' : ''}`,
    {
      'data-slot': at,
      style: item ? `--rune:${RUNE[item.rarity]}` : '',
      'aria-pressed': String(on),
      title: item ? (featReady(hero, item) ? `${itemName(item, hero.heroClass)} · ${featReadyText(hero, item)}` : itemName(item, hero.heroClass)) : wornLabel(at),
      onclick: () => {
        view.at = at;
        view.bag = 0;
        c.rerender();
      },
    },
    item
      ? pieceImg(c.renderer, itemThumbKey(item, hero.heroClass), 128)
      : held && hero.gear.weapon
        ? h('span.gs-ghost', {}, pieceImg(c.renderer, itemThumbKey(hero.gear.weapon, hero.heroClass), 128))
        : h('span.gs-glyph', { html: locked ? ICONS.lock : SLOT_GLYPH[at] }),
    item?.plus ? h('span.gt-plus', {}, `+${item.plus}`) : '',
    locked ? h('span.gs-lv', {}, t('gear.lv', { n: SLOT_LEVEL[at] })) : '',
    h('span.gs-name', {}, wornLabel(at)),
  );
};

/** The band above the doll: the gear's power, the weight set, and the materials the forge asks for. */
const summary = (c: GearCtx): HTMLElement => {
  const hero = c.p.hero;
  const worn = Object.values(hero.gear).filter((x): x is ItemInstance => !!x);
  const powered = worn.filter((x) => x.power !== undefined);
  const avg = powered.length ? Math.round(powered.reduce((a, x) => a + (x.power ?? 0), 0) / powered.length) : 0;
  const set = weightSetOf(hero);
  const g = gearMods(hero);
  const capped = [g.cooldownBp >= GEAR_CAPS.cooldownBp ? t('hero.capCooldown', { n: GEAR_CAPS.cooldownBp / 100 }) : '', g.armorBp >= GEAR_CAPS.armorBp ? t('hero.capArmor', { n: GEAR_CAPS.armorBp / 100 }) : ''].filter(Boolean);
  const chip = (icon: string, text: string, title = '', cls = ''): HTMLElement => h(`span.gs-chip${cls}`, { title }, h('span', { html: icon }), text);
  return h(
    'div.gs-summary',
    {},
    hero.level >= TITAN_MARK_LEVEL ? chip(ICONS.sparkle, t('gear.titan'), t('gear.titanHint'), '.titan') : '',
    avg ? chip(ICONS.sparkle, t('gear.avgPower', { n: avg }), t('gear.powerHint'), '.power') : '',
    set ? chip(ICONS.armor, t('gear.setOn', { w: t(`weight.${set}`), n: Math.round(WEIGHT_SET_BP / 100) }), t('gear.setHint'), '.set') : '',
    (hero.embers ?? 0) > 0 ? chip(ICONS.ember, t('temper.embers', { n: hero.embers ?? 0 }), t('temper.embersTip')) : '',
    (hero.shards ?? 0) > 0 ? chip(ICONS.shard, t('carve.shards', { n: hero.shards ?? 0 }), t('carve.shardsTip')) : '',
    ...SEAL_IDS.filter((k) => (hero.seals?.[k] ?? 0) > 0).map((k) => chip(ICONS.sparkle, t('seal.count', { n: hero.seals?.[k] ?? 0, s: t(`seal.${k}.short`) }), t(`seal.${k}`))),
    chip(ICONS.dust, t('gear.dust', { n: c.s.storage.dust }), t('gear.dustHint')),
    capped.length ? h('p.small.hint.gear-cap', { 'data-test': 'gear-cap' }, capped.join(' ')) : '',
  );
};

/** The gear tab: summary, the doll with its slots and the inspector, then the bag. */
export const gearPane = (c: GearCtx, salvage: { dust: Record<string, number>; plusBp: number }, portrait: string): HTMLElement => {
  const hero = c.p.hero;
  const cls = hero.heroClass;
  // Pieces gone since the last look (melted, gifted, equipped) leave nothing picked.
  if (view.bag && !hero.bag.some((x) => x.id === view.bag)) view.bag = 0;
  if (!view.bag && !view.at) view.at = 'weapon';
  fresh = freshIds(c.s, c.p);
  // The piece picked is being looked at.
  if (view.bag && fresh.delete(view.bag)) markSeen(c.s, c.p, [view.bag]);
  // Compile the pieces' cards ahead, one after another, so none of them freezes the page when drawn.
  const keys = [...Object.values(hero.gear), ...hero.bag].filter((x): x is ItemInstance => !!x).map((x) => itemThumbKey(x, cls));
  const box = h('div.hs-pane.gear-pane');
  void compileThumbs(c.renderer, keys, () => box.isConnected || !box.ownerDocument);

  const doll = h(
    'div.gs-doll',
    {},
    h('div.gs-col', {}, ...LEFT.map((w) => slotTile(c, w))),
    // Three-quarters front, so both hands (the shield, the orb, the second axe) are in view.
    h('div.gs-figure', {}, pieceImg(c.renderer, portrait, 512, 'gs-hero', FIGURE_YAW), h('div.gs-plinth')),
    h('div.gs-col', {}, ...RIGHT.map((w) => slotTile(c, w))),
  );
  const picked = view.bag ? hero.bag.find((x) => x.id === view.bag) : undefined;
  const at = view.at;
  const inspector = picked ? inspectBag(c, picked, salvage) : at && hero.gear[at] ? inspectWorn(c, at, hero.gear[at] as ItemInstance) : at ? inspectEmpty(c, at) : h('p.hint', {}, t('gear.pick'));
  if (flashed && !picked) flashed = 0;
  box.append(summary(c), h('div.gs-main', {}, doll, h('div.gs-inspect', {}, inspector)));

  // The bag: a filter by slot, then a grid, best first.
  const filter = h('div.bag-filter', { role: 'tablist' });
  if (view.filter !== 'all' && !hero.bag.some((i) => i.slot === view.filter)) view.filter = 'all';
  const chip = (key: typeof view.filter, label: string, n: number): HTMLElement =>
    h(
      `button.bag-chip${view.filter === key ? '.on' : ''}`,
      { role: 'tab', 'aria-selected': String(view.filter === key), onclick: () => { view.filter = key; c.rerender(); } },
      label,
      h('span', {}, String(n)),
      // New pieces of this kind: a dot, so they're found behind a filter too.
      key !== 'all' && hero.bag.some((i) => i.slot === key && fresh.has(i.id)) ? h('i.chip-new', { title: t('bag.fresh') }) : '',
    );
  filter.append(chip('all', t('hero.bagAll'), hero.bag.length));
  for (const sl of BAG_ORDER) {
    const n = hero.bag.filter((i) => i.slot === sl).length;
    if (n) filter.append(chip(sl, t(`slot.plural.${sl}`), n));
  }
  const total = (i: ItemInstance): number => Object.values(itemAttrs(i)).reduce((a: number, v) => a + (v ?? 0), 0);
  const items = hero.bag
    .filter((i) => view.filter === 'all' || i.slot === view.filter)
    // New pieces first, then by kind, rarity and strength.
    .sort((x, y) => Number(fresh.has(y.id)) - Number(fresh.has(x.id)) || BAG_ORDER.indexOf(x.slot) - BAG_ORDER.indexOf(y.slot) || (RARITY_ORDER[x.rarity] ?? 5) - (RARITY_ORDER[y.rarity] ?? 5) || (y.power ?? 0) - (x.power ?? 0) || total(y) - total(x) || x.id - y.id);
  // Melting every common piece at once (the uniques never; one command each, so the log keeps each piece).
  const commons = hero.bag.filter((x) => x.rarity === 'common' && !x.unique);
  const meltAll =
    c.cap && commons.length > 1
      ? h(
          'button.btn.small.ghost.melt-all',
          {
            'data-test': 'melt-commons',
            title: t('gear.meltCommonsHint'),
            onclick: async () => {
              for (const x of commons) if (!(await c.cmd({ t: 'salvage', item: x.id }))) break;
              view.bag = 0;
              audio.play('build', 0.35);
              c.rerender();
            },
          },
          t('gear.meltCommons', { n: commons.length }),
          ' ',
          h('span.cost-chip', {}, h('span', { html: ICONS.dust }), `+${commons.reduce((a, x) => a + salvageDust(x, salvage.dust, salvage.plusBp), 0)}`),
        )
      : '';
  noteShown(items.filter((x) => fresh.has(x.id)).map((x) => x.id));
  const seenAll = fresh.size
    ? h(
        'button.btn.small.ghost.seen-all',
        { 'data-test': 'seen-all', onclick: () => { markSeen(c.s, c.p, [...fresh]); c.rerender(); } },
        t('bag.seenAll', { n: fresh.size }),
      )
    : '';
  const bag = h('section.gs-bagwrap', {}, h('div.sec-title', {}, h('h4', {}, t('hero.bag')), seenAll, meltAll, h('span.small', {}, `${hero.bag.length} / ${MAX_BAG}`)));
  if (hero.bag.length) bag.append(filter, h('div.gs-bag', {}, ...items.map((x) => bagTile(c, x))), h('p.hint', {}, c.cap ? t('hero.bagHint') : c.ruined ? t('hero.bagHintForgeRuined') : t('hero.bagHintNoForge')));
  else bag.append(h('p.hint', {}, t('gear.bagEmpty')));
  box.append(bag, recipesSection(c), gemPouch(c), armorySection(c));
  return box;
};

/** A price chip, struck when it can't be paid. */
const cost = (icon: string, n: number, short: boolean): HTMLElement => h(`span.cost-chip${short ? '.short' : ''}`, {}, h('span', { html: icon }), String(n));

/**
 * The gem pouch (docs/EQUIPO.md §5): one card a gem, what is kept of each cut, cutting a chipped or a whole one from
 * the storage's crystal, and fusing three of a cut into one of the next. Settings are filled from the forge on a piece.
 */
const gemPouch = (c: GearCtx): HTMLElement => {
  const hero = c.p.hero;
  const dust = c.s.storage.dust;
  const crystal = c.s.storage.crystal;
  const box = h('section.gem-pouch', { 'data-test': 'gem-pouch' }, h('div.sec-title', {}, h('h4', {}, t('gems.title')), h('span.small', {}, `${Object.values(hero.gems ?? {}).reduce((a, n) => a + n, 0)}`)));
  if (!c.cap) {
    box.append(h('p.hint', {}, t(c.ruined ? 'forge.ruined' : 'gems.needForge')));
    return box;
  }
  const run = async (cmd: CampaignCommand): Promise<void> => {
    if (await c.cmd(cmd)) {
      audio.play('build', 0.35);
      c.rerender();
    }
  };
  const grid = h('div.gem-cards');
  for (const gem of GEMS) {
    const have = GEM_CUTS.map((cut) => hero.gems?.[`${gem}:${cut}`] ?? 0);
    const acts: HTMLElement[] = [];
    for (const cut of [0, 1] as const) {
      const pc = GEM_CUT_COST[cut]!;
      const ok = hero.level >= (GEM_CUT_LEVEL[cut] as number) && crystal >= pc.crystal && dust >= pc.dust;
      acts.push(h('button.btn.small.stone', { 'data-test': 'cut-gem', 'data-gem': gem, 'data-cut': String(cut), disabled: !ok, onclick: () => void run({ t: 'cutGem', gem, cut }) }, t('gems.cut', { cut: t(`gemCut.${GEM_CUT_KEYS[cut]}`) }), ' ', cost(ICONS.crystal, pc.crystal, crystal < pc.crystal), cost(ICONS.dust, pc.dust, dust < pc.dust)));
    }
    for (const to of [1, 2, 3, 4] as const) {
      if ((have[to - 1] ?? 0) < 3) continue;
      const f = GEM_FUSE[to];
      const lv = GEM_CUT_LEVEL[to] as number;
      if (hero.level < lv) {
        acts.push(h('p.small.hint', {}, t('gems.fuseLevel', { cut: t(`gemCut.${GEM_CUT_KEYS[to]}`), n: lv })));
        continue;
      }
      const ok = dust >= f.dust && (hero.embers ?? 0) >= f.embers && (hero.shards ?? 0) >= f.shards && (hero.seals?.tide ?? 0) >= f.tideSeal;
      acts.push(
        h(
          'button.btn.small',
          { 'data-test': 'fuse-gem', 'data-gem': gem, 'data-cut': String(to), disabled: !ok, onclick: () => void run({ t: 'fuseGems', gem, cut: to }) },
          t('gems.fuse', { cut: t(`gemCut.${GEM_CUT_KEYS[to]}`) }),
          ' ',
          f.embers ? cost(ICONS.ember, f.embers, (hero.embers ?? 0) < f.embers) : '',
          f.shards ? cost(ICONS.shard, f.shards, (hero.shards ?? 0) < f.shards) : '',
          f.tideSeal ? cost(ICONS.sparkle, f.tideSeal, (hero.seals?.tide ?? 0) < f.tideSeal) : '',
          cost(ICONS.dust, f.dust, dust < f.dust),
        ),
      );
    }
    grid.append(
      h(
        'div.gem-card',
        { style: `--gem:${GEM_COLOR[gem]}`, 'data-gem': gem },
        h('div.gem-card-head', {}, h('span.gem-ico', { html: ICONS.gem }), h('b', {}, t(`gem.${gem}`)), h('span.small', {}, t(`attrName.${GEM_ATTR[gem]}`))),
        h('div.gem-have', {}, ...GEM_CUTS.filter((cut) => (have[cut] ?? 0) > 0).map((cut) => h('span.gem-count', { 'data-cut': String(cut) }, `${t(`gemCut.${GEM_CUT_KEYS[cut]}`)} ×${have[cut]}`))),
        h('div.gem-acts', {}, ...acts),
      ),
    );
  }
  box.append(grid, h('p.hint', {}, t('gems.hint', { cap: GEM_CAP_BP / 100 })));
  return box;
};

/**
 * The forge's recipes the bastion knows (docs/SABERES.md §4.4): Hilde makes one piece of each for whoever asks,
 * once a Marea per hero. Absent until a recipe is known (rule 4: nothing tells they exist).
 */
const recipesSection = (c: GearCtx): HTMLElement | '' => {
  const known = RECIPE_BASE_IDS.filter((b) => c.s.saberes?.known.some((k) => k.id === RECIPE_SABER[b]));
  if (!known.length) return '';
  const box = h('section.recipes', { 'data-test': 'recipes' }, h('div.sec-title', {}, h('h4', {}, t('recipe.title'))), h('p.small.hint', {}, t('recipe.hint')));
  const made = c.p.hero.recipeMarea === calendarMarea(c.s);
  const dust = recipeDust(c.s);
  for (const base of known) {
    const wears = canWearBase(c.p.hero.heroClass, base);
    const why = !c.cap ? (c.ruined ? t('forge.ruined') : t('reason.needsForge')) : !wears ? t('recipe.cantWear') : made ? t('recipe.made') : '';
    const img = h('img.loading', { alt: '', draggable: 'false' }) as HTMLImageElement;
    thumbLater(c.renderer, `gear:${base}:uncommon`, 72, (url) => ((img.src = url), img.classList.remove('loading')), () => img.isConnected || !img.ownerDocument);
    box.append(
      h(
        'div.recipe',
        { 'data-base': base },
        h('span.recipe-pic', { style: `--rune:${RUNE.uncommon}` }, img),
        h(
          'div',
          {},
          h('b', {}, t(`gear.base.${base}`)),
          h('p.small', {}, traitLines(GEAR_BASES[base].trait, undefined).join(' · ')),
          why
            ? h('p.small.hint', {}, why)
            : h(
                'button.btn.small',
                { 'data-act': `recipe-${base}`, disabled: c.s.storage.dust < dust, onclick: async () => { if (await c.cmd({ t: 'recipe', base })) { audio.play('build', 0.5); c.rerender(); } } },
                t('recipe.make'),
                ' ',
                h('span.cost-chip', {}, h('span', { html: ICONS.dust }), String(dust)),
              ),
        ),
      ),
    );
  }
  return box;
};

/**
 * The group's armory (docs/EQUIPO.md §6): every base by slot, the best one found with who found it, the rest as
 * silhouettes. Folded until asked for, so its 44 cards are drawn only then (one a frame).
 */
const armorySection = (c: GearCtx): HTMLElement => {
  const all = armoryShowcase(c.s);
  // A forge recipe the bastion knows counts like any base; one it doesn't know isn't there at all (docs/SABERES.md §4.4).
  const bases = [...GEAR_BASE_IDS, ...RECIPE_BASE_IDS.filter((b) => c.s.saberes?.known.some((k) => k.id === RECIPE_SABER[b]))];
  const found = bases.filter((b) => all[b]).length;
  const box = h('section.armory', { 'data-test': 'armory' });
  box.append(
    h(
      'button.armory-toggle',
      { 'aria-expanded': String(view.armory), 'data-test': 'armory-toggle', onclick: () => { view.armory = !view.armory; c.rerender(); } },
      h('span.armory-ico', { html: ICONS.sword }),
      h('b', {}, t('armory.title')),
      h('span.armory-count', {}, t('armory.count', { n: found, of: bases.length })),
      h('span.chev', { 'aria-hidden': 'true' }, view.armory ? '▴' : '▾'),
    ),
  );
  if (!view.armory) return box;
  box.append(h('p.small.hint', {}, t('armory.hint')));
  for (const slot of BAG_ORDER) {
    const row = h('div.armory-grid');
    for (const base of bases.filter((b) => GEAR_BASES[b].slot === slot)) {
      const f = all[base];
      const who = f ? c.s.players.find((p) => p.id === f.pid)?.displayName ?? '—' : '';
      row.append(
        h(
          `div.armory-tile${f ? '' : '.missing'}`,
          { style: f ? `--rune:${RUNE[f.rarity]}` : '', 'data-base': base, title: t(`gear.base.${base}`) },
          h('div.armory-pic', {}, pieceImg(c.renderer, `gear:${base}:${f?.rarity ?? 'common'}`, 128)),
          h('b', {}, t(`gear.base.${base}`)),
          f ? h('span.small', {}, `${t(`rarity.${f.rarity}`)}${f.power ? ` · ${t('gear.power', { n: f.power })}` : ''}`) : h('span.small', {}, t('armory.missing')),
          f ? h('span.small.armory-who', {}, who) : '',
        ),
      );
    }
    box.append(h('h5.armory-slot', {}, t(`slot.plural.${slot}`)), row);
  }
  return box;
};

/** Clears what the sheet had picked (a new hero, a fresh look). */
export const resetGearView = (): void => {
  view.at = 'weapon';
  view.bag = 0;
  view.filter = 'all';
};
